/**
 * 날짜 관련 유틸리티 함수들
 * 모든 날짜는 한국 시간(KST, UTC+9) 기준으로 처리됩니다.
 */

/**
 * 한국 시간(KST) 기준으로 Date 객체를 생성합니다.
 * @returns 한국 시간으로 변환된 Date 객체
 */
export const getKSTDate = (): Date => {
  // 현재 UTC 시간에 9시간을 더해 한국 시간으로 변환
  const now = new Date();
  const kstOffset = 9 * 60 * 60 * 1000; // 9시간을 밀리초로
  return new Date(now.getTime() + kstOffset);
};

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환 (한국 시간 기준)
 * @returns 오늘 날짜 문자열 (KST)
 */
export const getTodayString = (): string => {
  const kstDate = getKSTDate();
  return kstDate.toISOString().split('T')[0];
};

/**
 * 날짜를 YYYY-MM-DD 형식으로 변환 (한국 시간 기준)
 * @param date Date 객체
 * @returns YYYY-MM-DD 형식 문자열 (KST)
 */
export const formatDateToString = (date: Date): string => {
  const kstOffset = 9 * 60 * 60 * 1000;
  const kstDate = new Date(date.getTime() + kstOffset);
  return kstDate.toISOString().split('T')[0];
};

/**
 * 오늘의 시작 시간 (00:00:00) 반환 (한국 시간 기준)
 * @returns 오늘 0시 Date 객체 (KST)
 */
export const getTodayStart = (): Date => {
  const kstDate = getKSTDate();
  const year = parseInt(kstDate.toISOString().slice(0, 4));
  const month = parseInt(kstDate.toISOString().slice(5, 7)) - 1; // JS month is 0-indexed
  const day = parseInt(kstDate.toISOString().slice(8, 10));
  
  // 한국 시간 00:00:00을 UTC로 변환 (9시간 빼기)
  const startOfDay = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  return new Date(startOfDay.getTime() - 9 * 60 * 60 * 1000);
};

/**
 * 오늘의 끝 시간 (23:59:59) 반환 (한국 시간 기준)
 * @returns 오늘 23:59:59 Date 객체 (KST)
 */
export const getTodayEnd = (): Date => {
  const kstDate = getKSTDate();
  const year = parseInt(kstDate.toISOString().slice(0, 4));
  const month = parseInt(kstDate.toISOString().slice(5, 7)) - 1;
  const day = parseInt(kstDate.toISOString().slice(8, 10));
  
  // 한국 시간 23:59:59을 UTC로 변환 (9시간 빼기)
  const endOfDay = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  return new Date(endOfDay.getTime() - 9 * 60 * 60 * 1000);
};

/**
 * 내일의 시작 시간 (00:00:00) 반환 (한국 시간 기준)
 * @returns 내일 0시 Date 객체 (KST)
 */
export const getTomorrowStart = (): Date => {
  const kstDate = getKSTDate();
  const year = parseInt(kstDate.toISOString().slice(0, 4));
  const month = parseInt(kstDate.toISOString().slice(5, 7)) - 1;
  const day = parseInt(kstDate.toISOString().slice(8, 10));
  
  // 내일 한국 시간 00:00:00을 UTC로 변환
  const tomorrow = new Date(Date.UTC(year, month, day + 1, 0, 0, 0, 0));
  return new Date(tomorrow.getTime() - 9 * 60 * 60 * 1000);
};

/**
 * 시간을 MM:SS 형식으로 포맷팅
 * @param seconds 초 단위 시간
 * @returns MM:SS 형식 문자열
 */
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 상대적 시간 표시 (예: "2시간 전", "방금 전")
 * @param date 기준 날짜
 * @returns 상대적 시간 문자열
 */
export const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;
  
  return date.toLocaleDateString('ko-KR');
};

/**
 * 한국 시간(Asia/Seoul)으로 날짜/시간을 포맷팅합니다.
 */
export const formatDateTimeKST = (
  date: Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
    ...options
  }).format(date);
};
